import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { HouseRules } from '../../../../components/society/HouseRules'
import { useModal } from '../../../../hooks/useModal'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { ButtonVariant } from '../../../../components/common/Button'
import { IconChoices } from '../../../../components/common/Icon'
// eslint-disable-next-line max-len
import { CreateUpdateHouseRuleModal } from '../../../../components/society/HouseRules/CreateUpdateHouseRuleModal'
import { ViewBase } from '../../../../components/common/ViewBase'
import { IllustrationChoices } from '../../../../components/common/Illustration'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { houseRuleQueries } from '../../../../api/house-rules'
import { i18nSocietyType } from '../../../../i18n'

export const HouseRulesView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const authUserId = useAuthenticatedUserId()
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('society:house-rules', {
      '{{ societyName }}': society?.name,
    })
  )
  const { data: rules, isLoading: isLoadingHouseRules } = useQuery(
    houseRuleQueries.getHouseRulesPerSociety(society?._id)
  )
  const {
    show: showCreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal()

  const isAdmin = isUserAdminInSociety(society, authUserId as string)

  const buttons = [
    ...(isAdmin
      ? [
          {
            icon: IconChoices.PLUS_SIGN,
            variant: ButtonVariant.PRIMARY,
            onClick: openCreateModal,
            label: translate('common.actions.add'),
          },
        ]
      : []),
  ]

  const loading = isLoadingHouseRules

  const errorView =
    !loading && rules && rules.length === 0
      ? {
          title: translate('houseRulesView.emptyState.title'),
          subtitle: translate('houseRulesView.emptyState.subtitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          }),
          illustration: IllustrationChoices.CHECKLIST,
        }
      : undefined

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('houseRulesView.title')}
        buttons={buttons}
        loading={loading}
        errorView={errorView}
      >
        <HouseRules />
      </ViewBase>
      {isAdmin && society && (
        <CreateUpdateHouseRuleModal
          society={society}
          show={showCreateModal}
          close={closeCreateModal}
        />
      )}
    </ErrorBoundary>
  )
}
